<script setup>
// Вкладення повідомлення: до пʼяти картинок або одне відео — і в чаті ефіру,
// і в коментарях.
//
// Місце під картинку резервуємо ще до того, як вона завантажилась (розміри
// приходять із сервера разом із повідомленням): інакше стрічка стрибала б на
// кожну підвантажену картинку, і чат, прокручений донизу, опинявся б посеред
// повідомлень.
//
// Спойлер — на всю пачку разом: автор ховає «оце все», а не кожен кадр
// окремо. Клік по відкритій картинці розгортає її на весь екран, стрілками
// листаються сусідні.

const props = defineProps({
  // Масив вкладень. Старі повідомлення приходили з одним медіа обʼєктом —
  // приймаємо й таке
  media: { type: [Array, Object], default: () => [] },
  spoiler: { type: Boolean, default: false },
  // Чат ефіру — вузька колонка, коментарі — ширший блок
  compact: { type: Boolean, default: false }
})

const toUrl = useStrapiMedia

const items = computed(() => {
  const list = Array.isArray(props.media) ? props.media : props.media ? [props.media] : []
  return list
    .filter((item) => item?.url)
    .map((item) => ({
      ...item,
      src: /^(?:https?:|blob:|data:)/.test(item.url) ? item.url : toUrl(item.url),
      video: String(item.mime || '').startsWith('video/')
    }))
})

const revealed = ref(false)
const hidden = computed(() => props.spoiler && !revealed.value)

// Одна картинка — у своїх пропорціях; кілька — плиткою квадратів
const single = computed(() => items.value.length === 1)
const columns = computed(() => (items.value.length === 2 || items.value.length === 4 ? 2 : 3))

const maxWidth = computed(() => (props.compact ? 240 : 360))
const maxHeight = computed(() => (props.compact ? 220 : 320))

// Розмір рамки під одну картинку, поки вона вантажиться. Без розмірів
// (старі повідомлення) — просто стеля за висотою
const singleBox = computed(() => {
  const item = items.value[0]
  if (!item?.width || !item?.height) return { maxWidth: `${maxWidth.value}px` }
  const scale = Math.min(1, maxWidth.value / item.width, maxHeight.value / item.height)
  return {
    width: `${Math.max(48, Math.round(item.width * scale))}px`,
    aspectRatio: `${item.width} / ${item.height}`,
    maxWidth: '100%'
  }
})

// --- Перегляд на весь екран ---
const viewer = ref(-1)

const open = (index) => {
  if (hidden.value) {
    revealed.value = true
    return
  }
  if (items.value[index]?.video) return
  viewer.value = index
}

const close = () => {
  viewer.value = -1
}

const images = computed(() => items.value.filter((item) => !item.video))

const step = (delta) => {
  if (viewer.value < 0) return
  const count = items.value.length
  let next = viewer.value
  // Відео в переглядачі не листаємо — воно грає на своєму місці
  for (let i = 0; i < count; i += 1) {
    next = (next + delta + count) % count
    if (!items.value[next].video) break
  }
  viewer.value = next
}

const onKey = (event) => {
  if (viewer.value < 0) return
  if (event.key === 'Escape') close()
  else if (event.key === 'ArrowRight') step(1)
  else if (event.key === 'ArrowLeft') step(-1)
  else return
  // Стрілки інакше перемотали б ще й плеєр під переглядачем
  event.preventDefault()
  event.stopPropagation()
}

watch(viewer, (index) => {
  if (!import.meta.client) return
  if (index >= 0) window.addEventListener('keydown', onKey, true)
  else window.removeEventListener('keydown', onKey, true)
})

onBeforeUnmount(() => {
  if (import.meta.client) window.removeEventListener('keydown', onKey, true)
})
</script>

<template>
  <div
    v-if="items.length"
    class="relative mt-1"
    :class="single ? 'inline-block align-top' : 'grid gap-1'"
    :style="
      single
        ? singleBox
        : { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, maxWidth: `${maxWidth}px` }
    "
  >
    <div
      v-for="(item, index) in items"
      :key="item.url + index"
      class="group/media relative bg-base-300 rounded overflow-hidden"
      :class="[
        single ? 'w-full h-full' : 'aspect-square',
        hidden || !item.video ? 'cursor-pointer' : ''
      ]"
      :title="hidden ? 'Натисніть, щоб показати' : ''"
      @click="open(index)"
    >
      <video
        v-if="item.video"
        :src="item.src"
        class="block w-full h-full"
        :class="[single ? 'object-contain max-h-[inherit]' : 'object-cover', hidden ? 'blur-xl scale-105 pointer-events-none' : '']"
        :style="single ? { maxHeight: `${maxHeight}px` } : null"
        :controls="!hidden"
        preload="metadata"
        playsinline
      />
      <img
        v-else
        :src="item.src"
        :width="item.width || undefined"
        :height="item.height || undefined"
        class="block w-full h-full transition-[filter] duration-200"
        :class="[single ? 'object-contain' : 'object-cover', hidden ? 'blur-xl scale-105' : '']"
        loading="lazy"
        decoding="async"
        alt=""
      />
    </div>

    <!-- Підказка поверх розмитого: без неї сірий прямокутник читається як
         зламана картинка, а не як «клікни, щоб подивитись» -->
    <button
      v-if="hidden"
      type="button"
      class="absolute inset-0 flex flex-col justify-center items-center gap-1 bg-base-300/40 rounded text-[11px] text-base-content/80"
      @click.stop="revealed = true"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
        <circle
          cx="12"
          cy="12"
          r="3"
        />
      </svg>
      Спойлер{{ items.length > 1 ? ` · ${items.length} вкладення` : '' }} — натисніть
    </button>

    <!-- Сховати знову: спойлер — стан показу, а не властивість повідомлення -->
    <button
      v-else-if="spoiler"
      type="button"
      class="top-1 left-1 absolute bg-base-300/90 opacity-0 group-hover:opacity-100 hover:opacity-100 px-1.5 py-0.5 rounded text-[10px] text-base-content/80 transition-opacity"
      @click.stop="revealed = false"
    >
      сховати
    </button>

    <Teleport
      v-if="viewer >= 0 && items[viewer]"
      to="body"
    >
      <!-- Вище за плаваюче вікно чату (z 2147483000, див. live/Chat.vue) -->
      <div
        class="z-[2147483002] fixed inset-0 flex justify-center items-center bg-black/90 p-4"
        @click.self="close"
      >
        <img
          :src="items[viewer].src"
          class="shadow-2xl rounded max-w-full max-h-full object-contain"
          alt=""
          @click.stop
        />

        <div class="top-3 right-3 absolute flex items-center gap-2">
          <span
            v-if="images.length > 1"
            class="text-white/70 text-xs tabular-nums"
            >{{ images.indexOf(items[viewer]) + 1 }} / {{ images.length }}</span
          >
          <a
            :href="items[viewer].src"
            target="_blank"
            rel="noopener noreferrer"
            class="bg-white/10 hover:bg-white/20 border-0 text-white btn btn-sm"
            title="Оригінал у новій вкладці"
            @click.stop
            >оригінал</a
          >
          <button
            class="bg-white/10 hover:bg-white/20 border-0 text-white btn btn-sm btn-circle"
            title="Закрити (Esc)"
            @click="close"
          >
            ✕
          </button>
        </div>

        <template v-if="images.length > 1">
          <button
            class="top-1/2 left-3 absolute bg-white/10 hover:bg-white/20 border-0 text-white -translate-y-1/2 btn btn-circle"
            title="Попередня (←)"
            @click.stop="step(-1)"
          >
            ‹
          </button>
          <button
            class="top-1/2 right-3 absolute bg-white/10 hover:bg-white/20 border-0 text-white -translate-y-1/2 btn btn-circle"
            title="Наступна (→)"
            @click.stop="step(1)"
          >
            ›
          </button>
        </template>
      </div>
    </Teleport>
  </div>
</template>
